import Checkbox from "@mui/joy/Checkbox";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Stack from "@mui/joy/Stack";
import { Permission } from "../../../models/auth";

interface PermissionSelectorProps {
  readonly selectedPermissions: Permission[];
  readonly onChange: (permissions: Permission[]) => void;
  readonly isCloudAuth?: boolean;
}

const permissionDescriptions = {
  [Permission.PATIENT]: "Access to patient data and notes",
  [Permission.DOCUMENT]: "Access to document management",
  [Permission.RPM]: "Access to Remote Patient Monitoring",
  [Permission.COHORT]: "Access to cohort management",
  [Permission.PROJECT]: "Access to research projects/studies",
  [Permission.ADMIN]: "Access to admin panel and user management",
  [Permission.IDENTIFYING_DATA]: "Access to non-pseudonymized data (names, identifiers)",
};

// For cloud users, limit available permissions
const cloudPermissions = [
  Permission.PATIENT,
  Permission.DOCUMENT,
  Permission.COHORT,
  Permission.PROJECT,
  Permission.IDENTIFYING_DATA,
];

export default function PermissionSelector({
  selectedPermissions,
  onChange,
  isCloudAuth = false,
}: PermissionSelectorProps) {
  const availablePermissions = isCloudAuth ? cloudPermissions : Object.values(Permission);

  const handlePermissionChange = (permission: Permission, checked: boolean) => {
    if (checked) {
      onChange([...selectedPermissions, permission]);
    } else {
      onChange(selectedPermissions.filter((p) => p !== permission));
    }
  };

  return (
    <FormControl>
      <FormLabel>Permissions</FormLabel>
      <Stack spacing={1}>
        {availablePermissions.map((permission) => (
          <Checkbox
            key={permission}
            label={permission}
            checked={selectedPermissions.includes(permission)}
            onChange={(e) => handlePermissionChange(permission, e.target.checked)}
          />
        ))}
      </Stack>
      <FormHelperText>
        {selectedPermissions.length > 0 && (
          <div>
            Selected permissions:
            <ul style={{ margin: "8px 0", paddingLeft: "16px" }}>
              {selectedPermissions.map((permission) => (
                <li key={permission} style={{ fontSize: "12px", marginBottom: "4px" }}>
                  <strong>{permission}:</strong> {permissionDescriptions[permission]}
                </li>
              ))}
            </ul>
          </div>
        )}
      </FormHelperText>
    </FormControl>
  );
}
